/**
 * NumberProvisioningStep — the real "go live → get your number" journey.
 *
 * The Plivo reseller flow, gated step by step (driven by the org's provisioning
 * state on sk-orchestrator): KYC (business + GST docs) → submitted/under review
 * → accepted → pick a number → buy → live. The AI answers that number once live.
 *
 * KYC approval is Plivo's (a compliance application must be 'accepted' before a
 * number can be bought); this screen submits, then polls, then unlocks buy.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, TextInput, ActivityIndicator } from 'react-native';
import { useTheme } from '../context/themeContext';
import { Button } from './voiceUi';
import { handleApiError } from '../utils/errorHandler';
import {
  getProvisioning, startProvisioning, submitKyc, pollKyc,
  listProvNumbers, buyProvNumber,
  type ProvState, type ProvNumber,
} from '../services/voice/numberProvisioning';

// E.164/spaced digits → grouped display, e.g. 918035340899 → "+91 80 3534 0899".
function prettyNumber(raw: string): string {
  const d = raw.replace(/\D/g, '');
  const n = d.startsWith('91') && d.length === 12 ? d.slice(2) : d;
  if (n.length === 10) return `+91 ${n.slice(0, 2)} ${n.slice(2, 6)} ${n.slice(6)}`;
  return raw;
}
const GSTIN = /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[Z][A-Z\d]$/;

export default function NumberProvisioningStep({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  const [state, setState] = useState<ProvState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // KYC form
  const [businessName, setBusinessName] = useState('');
  const [gstin, setGstin] = useState('');
  const [regDoc, setRegDoc] = useState<string | null>(null);   // filename ref
  const [gstDoc, setGstDoc] = useState<string | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);

  // Number pick
  const [numbers, setNumbers] = useState<ProvNumber[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setState(await getProvisioning(orgId)); }
    catch (e) { handleApiError(e, 'Could not load your number setup'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  // While KYC is under review, poll every 8s until it flips to accepted/rejected.
  const polling = state?.kyc_status === 'SUBMITTED';
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(async () => {
      try {
        const next = await pollKyc(orgId);
        setState(next);
      } catch { /* keep polling */ }
    }, 8000);
    return () => clearInterval(t);
  }, [polling, orgId]);

  // Load pickable numbers once KYC is accepted.
  useEffect(() => {
    if (state?.can_buy && numbers.length === 0) {
      void listProvNumbers(orgId).then(setNumbers).catch(() => {});
    }
  }, [state?.can_buy, numbers.length, orgId]);

  const onSubmitKyc = useCallback(async () => {
    setFormErr(null);
    if (businessName.trim().length < 2) return setFormErr('Enter your registered business name.');
    if (gstin.trim() && !GSTIN.test(gstin.trim().toUpperCase())) return setFormErr('That GSTIN doesn’t look valid.');
    if (!regDoc || !gstDoc) return setFormErr('Upload both the registration certificate and the GST certificate.');
    setBusy(true);
    try {
      const next = await submitKyc(orgId, {
        businessName: businessName.trim(),
        ...(gstin.trim() ? { gstin: gstin.trim().toUpperCase() } : {}),
        documents: { registration_certificate: regDoc, gst_certificate: gstDoc },
      });
      setState(next);
    } catch (e) { handleApiError(e, 'Could not submit KYC'); }
    finally { setBusy(false); }
  }, [orgId, businessName, gstin, regDoc, gstDoc]);

  const onBuy = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const next = await buyProvNumber(orgId, selected);
      setState(next);
      if (next.is_live) onDone();
    } catch (e) { handleApiError(e, 'Could not get the number'); }
    finally { setBusy(false); }
  }, [orgId, selected, onDone]);

  const label = { fontSize: 12, color: c.ink2, marginBottom: 4, fontFamily: theme.font.sans } as const;
  const input = {
    height: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong,
    borderRadius: 8, backgroundColor: c.card, color: c.ink, fontSize: 13,
  } as const;
  const mono = { fontFamily: theme.font.mono } as const;

  if (loading) return <View style={{ padding: 20, alignItems: 'center' }}><ActivityIndicator color={c.ink3} /></View>;

  // ── LIVE ──
  if (state?.is_live) {
    return (
      <View style={{ gap: 10 }}>
        <Text style={{ fontSize: 13, fontWeight: '700', color: c.outcomeOk }}>✓ You’re live</Text>
        <Text style={{ fontSize: 12.5, color: c.ink2, lineHeight: 18 }}>
          Your number {state.chosen_number ? prettyNumber(state.chosen_number) : ''} is wired to SK Voice — the AI answers calls to it now. Try calling it.
        </Text>
        <Button kind="primary" label="Continue" onPress={onDone} />
      </View>
    );
  }

  // ── STEP 3: pick + buy (KYC accepted) ──
  if (state?.can_buy) {
    return (
      <View style={{ gap: 12 }}>
        <Text style={{ fontSize: 13, fontWeight: '700', color: c.ink }}>KYC approved — pick your number</Text>
        <Text style={{ fontSize: 12, color: c.ink3, lineHeight: 17 }}>
          Choose a number for your salon. This becomes the line customers call — the AI answers it.
        </Text>
        {numbers.length === 0
          ? <View style={{ padding: 16, alignItems: 'center' }}><ActivityIndicator color={c.ink4} /></View>
          : numbers.map((n) => {
            const sel = selected === n.number;
            return (
              <Pressable key={n.number} onPress={() => setSelected(n.number)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 10,
                  borderWidth: 1, borderColor: sel ? c.ink : c.line, backgroundColor: sel ? c.paper2 : c.card }}>
                <Text style={{ fontSize: 16 }}>🇮🇳</Text>
                <View style={{ flex: 1 }}>
                  <Text style={[mono, { fontSize: 14, fontWeight: '600', color: c.ink }]}>{prettyNumber(n.number)}</Text>
                  <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 1 }}>
                    {n.region ?? 'Voice'}{n.assignedToOtherOrg ? ' · in use — will reassign' : ''}
                  </Text>
                </View>
                {sel ? <Text style={{ color: c.ink, fontWeight: '700' }}>✓</Text> : null}
              </Pressable>
            );
          })}
        {selected && (
          <Button kind="primary" label={busy ? 'Getting your number…' : 'Get this number & go live'} onPress={onBuy} disabled={busy} />
        )}
      </View>
    );
  }

  // ── STEP 2: KYC under review ──
  if (state?.kyc_status === 'SUBMITTED') {
    return (
      <View style={{ gap: 10, padding: 14, borderRadius: 10, borderWidth: 1, borderColor: c.line, backgroundColor: c.paper2 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <ActivityIndicator color={c.ink3} />
          <Text style={{ fontSize: 13, fontWeight: '700', color: c.ink }}>KYC under review</Text>
        </View>
        <Text style={{ fontSize: 12, color: c.ink3, lineHeight: 17 }}>
          We’ve submitted your documents. Indian numbers need a quick regulatory check (usually within a day). The moment it’s approved, you’ll pick your number here — this page updates on its own.
        </Text>
      </View>
    );
  }
  if (state?.kyc_status === 'REJECTED') {
    return (
      <View style={{ gap: 10 }}>
        <Text style={{ fontSize: 13, fontWeight: '700', color: c.outcomeMiss }}>KYC needs another look</Text>
        <Text style={{ fontSize: 12, color: c.ink3, lineHeight: 17 }}>Your documents couldn’t be verified. Re-check the business name matches your certificates exactly, then resubmit.</Text>
        <Button kind="default" label="Resubmit KYC" onPress={() => setState({ ...state, kyc_status: 'NONE' })} />
      </View>
    );
  }

  // ── STEP 1: KYC form ──
  return (
    <View style={{ gap: 12 }}>
      <Text style={{ fontSize: 12.5, color: c.ink2, lineHeight: 18 }}>
        To get a number we run a one-time KYC — a regulatory requirement for Indian phone numbers. Enter your business details and upload two documents; once approved you’ll pick your number.
      </Text>

      <View>
        <Text style={label}>Registered business name</Text>
        <TextInput value={businessName} onChangeText={setBusinessName}
          placeholder="As on your Certificate of Incorporation / Udyam" placeholderTextColor={c.ink4}
          style={input} />
      </View>

      <View>
        <Text style={label}>GSTIN (optional)</Text>
        <TextInput value={gstin} onChangeText={setGstin} autoCapitalize="characters"
          placeholder="29ABCDE1234F1Z5" placeholderTextColor={c.ink4} style={[input, mono]} />
      </View>

      <DocUpload label="Registration certificate (Incorporation / Udyam)" file={regDoc} onPick={setRegDoc} c={c} />
      <DocUpload label="GST certificate (Form GST REG-06)" file={gstDoc} onPick={setGstDoc} c={c} />

      {formErr && <Text style={{ fontSize: 11.5, color: c.outcomeMiss }}>{formErr}</Text>}

      <Button kind="primary" label={busy ? 'Submitting…' : 'Submit KYC'} onPress={onSubmitKyc} disabled={busy} />
    </View>
  );
}

// Minimal doc picker — web file input; stores the filename as the doc reference.
// (In LOCAL mode the bytes aren't shipped to Plivo yet; the ref represents the
// uploaded document. Swaps to a real upload when Plivo compliance is switched on.)
function DocUpload({ label, file, onPick, c }: {
  label: string; file: string | null; onPick: (name: string | null) => void; c: Record<string, string>;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <View>
      <Text style={{ fontSize: 12, color: c.ink2, marginBottom: 4 }}>{label}</Text>
      <Pressable
        onPress={() => ref.current?.click()}
        style={{ height: 40, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderStyle: 'dashed',
          borderColor: file ? c.outcomeOk : c.lineStrong, backgroundColor: c.card, justifyContent: 'center' }}>
        <Text style={{ fontSize: 12.5, color: file ? c.ink : c.ink4 }}>
          {file ? `✓ ${file}` : '＋ Upload file (PDF / image)'}
        </Text>
      </Pressable>
      <input ref={ref} type="file" accept=".pdf,image/*" style={{ display: 'none' }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPick(e.target.files?.[0]?.name ?? null)} />
    </View>
  );
}
